/**
 * @file chatServiceAPI.ts
 * @author Arunkumar Annamalai
 * @version 1.0.0
 * @description Chat Service API client for managing chat functionality.
 *              This service handles all chat-related API calls including
 *              tokens, conversations, messages, and usage.
 * @created Aug-2025
 * @lastModified Aug-2025
 */

import api from './api';
import { ChatToken } from '../types';

export interface Conversation {
  id: string;
  title: string;
  companyId: string;
  userId?: string;
  status: string;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string;
  userEmail?: string;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderType: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: any;
  tokensUsed: number;
  costCents: number;
  createdAt: string;
}

export interface ChatUsage {
  date: string;
  totalTokens: number;
  totalCostCents: number;
  totalConversations: number;
}

export interface ChatSettings {
  chatEnabled: boolean;
  chatLimitDaily: number;
  chatCostPerToken: number;
}

export interface CreateChatTokenRequest {
  name: string;
  chatService: string;
  apiToken: string;
  companyId: string;
}

export interface UpdateChatTokenRequest {
  name: string;
  chatService: string;
  apiToken: string;
}

export interface CreateConversationRequest {
  title: string;
  metadata?: any;
}

export interface SendMessageRequest {
  content: string;
  senderType: 'user' | 'assistant' | 'system';
  metadata?: any;
  tokensUsed?: number;
  costCents?: number;
}

class ChatServiceAPI {
  private baseURL = '/chatService';

  // Chat Tokens
  async getChatTokens(): Promise<ChatToken[]> {
    const response = await api.get(`${this.baseURL}/tokens`);
    return response.data;
  }

  async createChatToken(data: CreateChatTokenRequest): Promise<ChatToken> {
    const response = await api.post(`${this.baseURL}/tokens`, data);
    return response.data;
  }

  async updateChatToken(id: string, data: UpdateChatTokenRequest): Promise<ChatToken> {
    const response = await api.put(`${this.baseURL}/tokens/${id}`, data);
    return response.data;
  }

  async deleteChatToken(id: string): Promise<void> {
    await api.delete(`${this.baseURL}/tokens/${id}`);
  }

  // Conversations
  async getConversations(): Promise<Conversation[]> {
    const response = await api.get(`${this.baseURL}/conversations`);
    return response.data;
  }

  async createConversation(data: CreateConversationRequest): Promise<Conversation> {
    const response = await api.post(`${this.baseURL}/conversations`, data);
    return response.data;
  }

  // Messages
  async getMessages(conversationId: string): Promise<ChatMessage[]> {
    const response = await api.get(`${this.baseURL}/conversations/${conversationId}/messages`);
    return response.data;
  }

  async sendMessage(conversationId: string, data: SendMessageRequest): Promise<ChatMessage> {
    const response = await api.post(`${this.baseURL}/conversations/${conversationId}/messages`, data);
    return response.data;
  }

  // Usage and Settings
  async getUsage(): Promise<ChatUsage[]> {
    const response = await api.get(`${this.baseURL}/usage`);
    return response.data;
  }

  async getSettings(): Promise<ChatSettings> {
    const response = await api.get(`${this.baseURL}/settings`);
    return response.data;
  }

  // Token Validation
  async validateToken(apiToken: string, chatService: string): Promise<{
    isValid: boolean;
    message: string;
    modelInfo?: {
      availableModels: string[];
      totalModels: number;
    };
  }> {
    const response = await api.post(`${this.baseURL}/validateToken`, {
      apiToken,
      chatService
    });
    return response.data;
  }

  // Get available companies (without chat services)
  async getAvailableCompanies(): Promise<{ id: string; name: string }[]> {
    const response = await api.get(`${this.baseURL}/companies/available`);
    return response.data;
  }

  // Get all chat tokens across all companies (for root admins)
  async getAllChatTokens(): Promise<ChatToken[]> {
    const response = await api.get(`${this.baseURL}/tokens/all`);
    return response.data;
  }
}

// Admin-specific chat service methods
class AdminChatServiceAPI extends ChatServiceAPI {
  async getAllChatTokens(): Promise<ChatToken[]> {
    const response = await api.get('/admin/chatTokens');
    return response.data;
  }

  async getCompanyChatUsage(companyId: string): Promise<ChatUsage[]> {
    const response = await api.get(`/admin/chatUsage/${companyId}`);
    return response.data;
  }

  async getCompanyChatConversations(companyId: string): Promise<Conversation[]> {
    const response = await api.get(`/admin/chatConversations/${companyId}`);
    return response.data;
  }
}

export const chatServiceAPI = new ChatServiceAPI();
export const adminChatServiceAPI = new AdminChatServiceAPI();
